import _ from 'lodash'
import { PRODUCT_SHIP_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import safeAwait from '../../../../../lib/safeAwait'
import errorHelper, { httpHandler } from '../../../../../lib/errorHelper'
import { ATTRIBUTE_CONSTRAINTS_TYPE } from '../../../../constants/proposalTypes'
import {
  getAttrLevelFromListing,
  getAttrLevelFromProduct
} from '../helpers/getters'
import {
  FORM_SECTION_NAME as TABLE_FORM_SECTION_NAME
} from './ModelTable'
import {
  PRODUCT_KEY,
  LISTING_KEY_MAP,
  ONE_LAYER_PRODUCT_KEY_MAP,
  TWO_LAYER_PRODUCT_KEY_MAP,
  ONE_LAYER_ATTRS,
  TWO_LAYER_ATTRS,
  PRODUCTS_MAX_SIZE,
  LISTING_CHANGE_STATUS
} from './constants'

export const getAttrMap = (isTwoLayerAttr) => {
  const productKeys = isTwoLayerAttr
    ? TWO_LAYER_ATTRS
    : ONE_LAYER_ATTRS
  const [
    productId,
    productName,
    productPartNo,
    specName,
    specSelectedValue,
    stock,
    barcode,
    specVisible,
    attr,
    availableCount,
    attributes
  ] = productKeys
  const layerAttrMap = {
    productId,
    productName,
    productPartNo,
    specName,
    specSelectedValue,
    stock,
    barcode,
    specVisible,
    attr,
    availableCount,
    attributes
  }
  return layerAttrMap
}

export const getFirstImg = model => _.get(model, 'images[0].url')

export const getListingOneLayerAttrInfo = (model) => {
  const oneLayerAttrInfo = _.reduce(ONE_LAYER_ATTRS, (collect, key) => {
    const attrValue = _.get(model, LISTING_KEY_MAP[key])
    if (!_.isUndefined(attrValue)) {
      _.set(collect, key, attrValue)
    }
    return collect
  }, {})
  return oneLayerAttrInfo
}

export const getListingTwoLayerAttrInfo = (item) => {
  const twoLayerAttrInfo = _.reduce(TWO_LAYER_ATTRS, (collect, key) => {
    const attrValue = _.get(item, `${LISTING_KEY_MAP[key]}`.replace('item.', ''))
    if (!_.isUndefined(attrValue)) {
      _.set(collect, key, attrValue)
    }
    return collect
  }, {})
  // overview 時取得的賣場資料是新的 updateListingModels proposal 格式
  // 所以商品編號變成 id 而不是 sku
  // https://tw.supplier.yahoo.com/docs/api/resources/proposal/#proposalitem
  const twoLayerAttrId = _.get(item, 'id')
  if (!_.isUndefined(twoLayerAttrId)) {
    _.set(twoLayerAttrInfo, PRODUCT_KEY.ITEM_ID, twoLayerAttrId)
  }
  return twoLayerAttrInfo
}

export const getTableRowsFromListing = (listing = {}, isProposalListing = false) => {
  let tableRows = []
  const {
    models = []
  } = listing
  const attrLevel = getAttrLevelFromListing(listing)
  const commonFields = [
    PRODUCT_KEY.IMAGES,
    PRODUCT_KEY.ATTRIBUTES
  ]

  if (attrLevel !== 2) {
    tableRows = _.map(models, model => {
      const oneLayerAttrInfo = getListingOneLayerAttrInfo(model)
      const {
        [PRODUCT_KEY.ID]: id,
        [PRODUCT_KEY.SPEC_SELECTED_VALUE]: specSelectedValue
      } = oneLayerAttrInfo
      return {
        attrLevel,
        image: getFirstImg(model),
        ..._.pick(model, commonFields),
        ...oneLayerAttrInfo,
        ...(
          _.isUndefined(id) && {
            [PRODUCT_KEY.ID]: getFakeProductId(specSelectedValue),
            [PRODUCT_KEY.IS_NEW]: true
          }
        )
      }
    })
    return tableRows
  }

  tableRows = _.reduce(models, (collect, model, index) => {
    const {
      items = []
    } = model
    const oneLayerAttrInfo = getListingOneLayerAttrInfo(model)
    const {
      [PRODUCT_KEY.ID]: id,
      [PRODUCT_KEY.SPEC_SELECTED_VALUE]: specSelectedValue
    } = oneLayerAttrInfo
    collect = _.concat(
      collect,
      _.map(items, (item, itemIndex) => {
        const twoLayerAttrInfo = getListingTwoLayerAttrInfo(item)
        const {
          [PRODUCT_KEY.ITEM_ID]: itemId,
          [PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE]: itemSpecSelectedValue
        } = twoLayerAttrInfo
        const tableRow = {
          attrLevel,
          image: getFirstImg(model),
          // 修改頁會從 get listing models 組出表格列，共用欄位不管單雙層都在 proposalModel
          // 確認、檢視頁圖片共用欄位會按照單雙層，單層放 proposalModel，雙層放 ProposalModelItem
          ..._.pick(isProposalListing ? item : model, commonFields),
          ...oneLayerAttrInfo,
          ...twoLayerAttrInfo,
          ...(_.isUndefined(id) && { [PRODUCT_KEY.ID]: getFakeProductId(specSelectedValue) }),
          ...(
            _.isUndefined(itemId) && {
              [PRODUCT_KEY.ITEM_ID]: getFakeProductId(itemSpecSelectedValue),
              [PRODUCT_KEY.IS_NEW]: true
            }
          )
        }
        _.set(tableRow, 'collection', _.get(tableRow, PRODUCT_KEY.SPEC_SELECTED_VALUE))
        return tableRow
      })
    )
    return collect
  }, [])
  return tableRows
}

export const getTableRowsWithoutDuplicated = tableRows => {
  const attrMap = getAttrMap(_.get(tableRows, '0.attrLevel') === 2)
  // 重複商品編號只保留第一個商品
  const tableRowsWithoutDuplicated = _.uniqBy(tableRows, row => _.get(row, attrMap.productId))
  return tableRowsWithoutDuplicated
}

export const getTableRowFromProduct = (product = {}, defaultNames = {}, isTwoLayerAttr) => {
  let tableRow = {}
  const productWithSpec = getProductWithSpec(product, defaultNames, isTwoLayerAttr)
  const attrLevel = getAttrLevelFromProduct(productWithSpec)
  const defaultVisibleAttrs = { [PRODUCT_KEY.SPEC_VISIBLE]: true }
  const [attrKeyMap, visibleAttrs] = isTwoLayerAttr
    ? [
      TWO_LAYER_PRODUCT_KEY_MAP,
      { ...defaultVisibleAttrs, [PRODUCT_KEY.ITEM_SPEC_VISIBLE]: true }
    ]
    : [ONE_LAYER_PRODUCT_KEY_MAP, defaultVisibleAttrs]
  const attrs = _.keys(attrKeyMap)
  const attributes = _.get(productWithSpec, PRODUCT_KEY.ATTRIBUTES, [])
  _.set(attributes, '0.name', defaultNames[PRODUCT_KEY.SPEC_NAME])
  if (isTwoLayerAttr) {
    _.set(attributes, '1.name', defaultNames[PRODUCT_KEY.ITEM_SPEC_NAME])
  }
  tableRow = {
    ..._.reduce(attrs, (collect, key) => {
      _.set(collect, key, _.get(productWithSpec, `${attrKeyMap[key]}`))
      return collect
    }, {}),
    ...defaultNames,
    ...visibleAttrs,
    attributes,
    image: getFirstImg(productWithSpec),
    images: _.get(product, PRODUCT_KEY.IMAGES, []),
    [PRODUCT_KEY.IS_NEW]: true,
    attrLevel
  }
  if (isTwoLayerAttr) {
    const specSelectedValue = _.get(tableRow, PRODUCT_KEY.SPEC_SELECTED_VALUE)
    // sortable table 用來決定分群拖曳的欄位
    _.set(tableRow, 'collection', specSelectedValue)
    // 雙層屬性商品匯入時 sku 是對應第二層屬性，
    // 但前端需要判別第一層的分類因此需要產生一個假的 id 對應第一層
    _.set(tableRow, PRODUCT_KEY.ID, getFakeProductId(specSelectedValue))
  }
  return tableRow
}

export const getGroupedTableRows = (tableRows, isTwoLayerAttr) => {
  const groupedRows = _.groupBy(tableRows, row => {
    const productSpec = _.get(row, PRODUCT_KEY.SPEC_SELECTED_VALUE)
    if (isTwoLayerAttr) {
      return productSpec
    }

    // 單層屬性時全部表格列都是獨立的，所以加上 productId 避免分到同一群
    const productId = _.get(row, PRODUCT_KEY.ID)
    return `${productId}_${productSpec}`
  })
  return groupedRows
}

export const getTableRowsFromGroupedRows = (groupedRows, specSortList, customize) => {
  const tableRows = _.reduce(specSortList, (collect, spec) => {
    const currentIndex = _.size(collect)
    collect = _.concat(
      collect,
      _.map(
        _.get(groupedRows, spec, []),
        // 加上流水號 react-sortable-hoc 會需要這個用來判斷排序
        (row, index) => {
          const rowWithIndex = { ...row, index: currentIndex + index }
          return customize ? customize(rowWithIndex) : rowWithIndex
        }
      )
    )
    return collect
  }, [])
  return tableRows
}

export const mergeImportRowsByGroup = (currentGroupedRows, importGroupedRows) => {
  const mergedTableRows = _.mergeWith(
    currentGroupedRows,
    importGroupedRows,
    // 合併兩個 object 同樣 key 裡面的陣列
    (groupRows, importRows) => {
      if (_.isArray(groupRows)) {
        return groupRows.concat(importRows)
      }
    }
  )
  return mergedTableRows
}

export const getGroupedRowsWithProductIndex = (groupedRows, specSortList) => {
  const clonedGroupedRows = _.cloneDeep(groupedRows)
  const isTwoLayerAttr = _.isUndefined(
    _.get(groupedRows, `['${_.head(specSortList)}'].${PRODUCT_KEY.ITEM_ID}`)
  )
  let currentIndex = 0
  for (const key of specSortList) {
    const rows = _.get(clonedGroupedRows, [key], [])
    for (const [rowIndex] of rows.entries()) {
      // 避免 set key 含有 `.` 變成巢狀物件
      // https://github.com/lodash/lodash/issues/3529#issuecomment-448586433
      const rowKey = `['${key}'][${rowIndex}]`
      _.set(clonedGroupedRows, `${rowKey}.${PRODUCT_KEY.INDEX}`, currentIndex)
      if (isTwoLayerAttr) {
        _.set(clonedGroupedRows, `${rowKey}.${PRODUCT_KEY.ITEM_INDEX}`, rowIndex)
      }
      currentIndex++
    }
  }
  return clonedGroupedRows
}

export const getTableField = (productId, column, record) => `${productId}_${column}_${record.index}`

export const getReduxFormField = (productId, column, record) => {
  return `${TABLE_FORM_SECTION_NAME}.${getTableField(productId, column, record)}`
}

export const getListingSpecNames = (sortList, groupedRows) => {
  const firstRowKey = `['${_.head(sortList)}'][0]`
  const specName = _.get(groupedRows, `${firstRowKey}.${PRODUCT_KEY.SPEC_NAME}`)
  const itemSpecName = _.get(groupedRows, `${firstRowKey}.${PRODUCT_KEY.ITEM_SPEC_NAME}`)
  return { specName, itemSpecName }
}

export const getProductsOverLimitMsg = (specList, groupedRows, isTwoLayerAttr) => {
  const { specName, itemSpecName } = getListingSpecNames(specList, groupedRows)
  const attrSize = _.size(specList)
  const attrMaxSize = isTwoLayerAttr
    ? PRODUCTS_MAX_SIZE.TWO_LAYER.PARENT_ATTR
    : PRODUCTS_MAX_SIZE.ONE_LAYER.ATTR
  if (attrSize > attrMaxSize) {
    return `${specName} 超過 ${attrMaxSize} 組`
  }

  if (isTwoLayerAttr) {
    const parentAttrMaxSize = PRODUCTS_MAX_SIZE.TWO_LAYER.ATTR
    const overLimitSpec = _.find(specList, spec => {
      const parentAttrSize = _.size(_.get(groupedRows, `${spec}`))
      return parentAttrSize > parentAttrMaxSize
    })
    if (overLimitSpec) {
      return `${specName}-${overLimitSpec} ${itemSpecName} 超過 ${parentAttrMaxSize} 組`
    }
  }

  return null
}

export const getApiFieldsFromRow = (row, isTwoLayerAttr) => {
  const map = isTwoLayerAttr
    ? TWO_LAYER_PRODUCT_KEY_MAP
    : ONE_LAYER_PRODUCT_KEY_MAP
  const apiFields = _.reduce(row, (fields, value, key) => {
    const apiField = _.get(map, key, key)
    fields[apiField] = value
    return fields
  }, {})
  return apiFields
}

export const getNewProductModelPropsFromRow = (row, isTwoLayerAttr) => {
  const attrMap = getAttrMap(isTwoLayerAttr)
  const defaultKeys = [
    attrMap.stock,
    attrMap.barcode,
    attrMap.productPartNo
  ]
  const defaultProps = _.flow(
    () => _.pick(row, defaultKeys),
    pickedRow => _.pickBy(pickedRow, fieldValue => !_.isUndefined(fieldValue)),
    filteredRow => getApiFieldsFromRow(filteredRow, isTwoLayerAttr)
  )()
  // 設定送出時第一二層屬性(attributes)的值(values)
  const attrProps = _.flow(
    // 單層屬性賣場只會有一層屬性
    // 雙層屬性賣場會有兩層
    () => _.times(isTwoLayerAttr ? 2 : 1, index => {
      const currentAttrMap = getAttrMap(index === 1)
      const attrValue = row[currentAttrMap.attr]
      return { name: row[currentAttrMap.specName], values: attrValue }
    }),
    attrValues => ({ [PRODUCT_KEY.ATTRIBUTES]: attrValues })
  )()
  const isSharedImages = !_.get(row, PRODUCT_KEY.EDITABLE)
  const newProductImages = (
    // 新增時從新增商品視窗拿圖片會在 NEW_PRODUCT_IMAGES
    // get 不帶預設值才會往下一個找圖片來源
    _.get(row, PRODUCT_KEY.NEW_PRODUCT_IMAGES) ||
    // 送出後從 API 回應拿圖片會在 IMAGES
    _.get(row, PRODUCT_KEY.IMAGES, [])
  )
  const newProductModelProps = {
    ...defaultProps,
    ...attrProps,
    ...(
      !isSharedImages &&
      ({ [PRODUCT_KEY.IMAGES]: newProductImages })
    )
  }
  return newProductModelProps
}

export const convertRowsToModel = (rows, isTwoLayerAttr) => {
  const row = _.head(rows)
  const sku = _.get(row, PRODUCT_KEY.ID)
  const isFakeSku = checkIsFakeProductId(sku)
  const attrMap = getAttrMap(isTwoLayerAttr)
  const defaultModel = {
    // 單層賣場非新增商品
    ...(!isFakeSku && !isTwoLayerAttr && {
      name: _.get(row, attrMap.productName),
      attributes: _.get(row, attrMap.attributes)
    }),
    // 假的 sku 不傳給後端
    ...(!isFakeSku && { sku }),
    ...(
      (isFakeSku && !isTwoLayerAttr) &&
      getNewProductModelPropsFromRow(row, isTwoLayerAttr)
    ),
    spec: {
      name: _.get(row, PRODUCT_KEY.SPEC_NAME),
      selectedValue: _.get(row, PRODUCT_KEY.SPEC_SELECTED_VALUE)
    },
    isVisible: _.get(row, PRODUCT_KEY.SPEC_VISIBLE)
  }
  if (!isTwoLayerAttr) {
    return defaultModel
  }

  const items = _.map(rows, row => {
    const id = _.get(row, PRODUCT_KEY.ITEM_ID)
    const isFakeId = checkIsFakeProductId(id)
    return {
      // 假的 id 不傳給後端
      ...(!isFakeId && {
        id,
        // 雙層層賣場非新增商品
        name: _.get(row, attrMap.productName),
        attributes: _.get(row, attrMap.attributes)
      }),
      ...(
        (isFakeId && isTwoLayerAttr) &&
        getNewProductModelPropsFromRow(row, isTwoLayerAttr)
      ),
      spec: {
        name: _.get(row, PRODUCT_KEY.ITEM_SPEC_NAME),
        selectedValue: _.get(row, PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE)
      },
      isVisible: _.get(row, PRODUCT_KEY.ITEM_SPEC_VISIBLE)
    }
  })
  return { ...defaultModel, items }
}

export const convertGroupedRowsToModels = (specList, groupedRows, isTwoLayerAttr) => {
  const models = _.map(specList, key => {
    const rows = _.get(groupedRows, key, [])
    const model = convertRowsToModel(rows, isTwoLayerAttr)
    return model
  })
  return models
}

/**
 * 取得賣場屬性調整表格的屬性排序、表格列、表格列按照屬性分群排序
 * @param {*} defaultTableInfo 預設表格狀態，要取得新的 specSortList sortedTableRows 需要帶入預設 specSortList
 * @param {*} newRows 新的表格列資料，defaultGroupedRows 有帶入的話會按照屬性分群後合併到後面
 * @param {*} isTwoLayerAttr 是否為雙層屬性賣場
 * @returns specSortList(屬性排序) sortedTableRows(表格列資料) 表格列按照屬性分群排序(groupedRowsWithIndex)
 */
export const getConvertedTableInfo = (defaultTableInfo = {}, newRows = [], isTwoLayerAttr) => {
  const {
    groupedRows: defaultGroupedRows = {},
    specSortList: defaultSpecSortList = []
  } = defaultTableInfo
  // 無屬性放在表格最底部
  // 單層只有單一屬性不會重複，新加入屬性會全部放在表格最底部
  // 雙層屬性時按照 spec 分群加入表格
  const newGroupBySpecTableRows = mergeImportRowsByGroup(
    defaultGroupedRows,
    getGroupedTableRows(newRows, isTwoLayerAttr)
  )
  // 將原先 spec 排序與匯入新的 spec 合併，以原先 spec 排序為主
  // 如果沒有預設排序 (defaultSpecSortList) 以分群結果 (newGroupBySpecTableRows) 取出排序數字的屬性會直接置頂排序
  // 移除不在目前表格內的 spec
  const newSpecSortList = _.flow(
    () => _.union(
      defaultSpecSortList,
      _.keys(newGroupBySpecTableRows)
    ),
    unionSpecList => _.filter(unionSpecList, spec => (spec in newGroupBySpecTableRows))
  )()
  // 加上分群的流水號
  const newGroupBySpecTableRowsWithIndex = getGroupedRowsWithProductIndex(
    newGroupBySpecTableRows,
    newSpecSortList
  )
  // 加上表格列排序的流水號
  const sortedTableRows = getTableRowsFromGroupedRows(
    newGroupBySpecTableRowsWithIndex,
    newSpecSortList
  )
  return {
    sortedTableRows,
    specSortList: newSpecSortList,
    groupedRowsWithIndex: newGroupBySpecTableRowsWithIndex
  }
}

export const checkIsListingChanged = async (actions, initialForm, listingId) => {
  const { fetchSpecificListing } = actions
  const params = { id: listingId, fields: [] }
  const [error, payload] = await safeAwait(
    fetchSpecificListing(params).then(httpHandler)
  )
  const errorMessage = errorHelper.parseErrorMessage(error)
  // 比對賣場變更時取得賣場失敗也當作變更不讓賣場繼續執行
  if (errorMessage) {
    return [LISTING_CHANGE_STATUS.FAIL, errorMessage]
  }
  const COMPARE_KEY = 'models'
  const defaultModels = _.get(initialForm, COMPARE_KEY)
  const currentModels = _.get(payload, COMPARE_KEY)
  // 轉換成字串後比較載入頁面時和送出前的商品屬性差異
  // 不一致代表已變更
  const isListingChanged = !_.isEqual(
    ..._.map([defaultModels, currentModels], JSON.stringify)
  )
  return [
    isListingChanged ? LISTING_CHANGE_STATUS.DIFF : LISTING_CHANGE_STATUS.SAME,
    undefined
  ]
}

export const getModifiedRowMap = (models, isTwoLayerAttr, mapper) => {
  const attrMap = getAttrMap(isTwoLayerAttr)
  let modelsWithCorrectSku = models
  if (isTwoLayerAttr) {
    // 雙層時 model 帶虛主鍵的 sku，真正的 sku 在 items 裡的 id
    modelsWithCorrectSku = _.map(models, model => {
      return {
        ...model,
        items: _.flow(
          model => _.get(model, 'items', []),
          // 新增是新增提案的，所以屬於 proposal model (id)
          // 刪除是刪掉賣場的，所以屬於 listing model (sku)
          items => _.map(items, ({ id, sku, ...item }) => ({ ...item, sku: id || sku }))
        )(model)
      }
    })
  }
  const tableRows = getTableRowsFromListing({ models: modelsWithCorrectSku }, true)
  const currentMapKey = mapper || attrMap.productId
  const modifiedRowMap = _.omit(
    _.keyBy(tableRows, currentMapKey),
    // 分群出來如果對應欄位為 undefined 就拿掉，避免 key 剛好也是 undefined 會拿到錯誤的欄位
    'undefined'
  )
  return modifiedRowMap
}

export const getUpdatedRowMap = (updatedModels) => {
  const updatedRowMap = _.flow(
    // 按照 sku 將更新的內容分群，單個 sku 可以同時有多個更新內容
    () => _.groupBy(updatedModels, 'sku'),
    // 將每一個 sku 的全部更新合併成一個
    groupModelsBySku => _.transform(groupModelsBySku, (rowMap, models, sku) => {
      rowMap[sku] = _.merge(...models)
      return rowMap
    }, {})
  )()
  return updatedRowMap
}

export const getProductWithSpec = (product, defaultNames, isTwoLayerAttr) => {
  const productAttrs = _.flow(
    () => _.get(product, 'structuredData.attributes', []),
    attributes => {
      const attributeMapByName = _.keyBy(attributes, 'name')
      const listingAttributeNameFields = isTwoLayerAttr
        ? [PRODUCT_KEY.SPEC_NAME, PRODUCT_KEY.ITEM_SPEC_NAME]
        : [PRODUCT_KEY.SPEC_NAME]
      const listingAttributes = _.map(listingAttributeNameFields, listingAttributeNameField => {
        const listingAttributeName = _.get(defaultNames, listingAttributeNameField)
        return _.get(attributeMapByName, listingAttributeName, {})
      })
      return listingAttributes
    }
  )()
  if ('spec' in product) {
    return { ...product, attributes: productAttrs }
  }

  const specName = defaultNames[PRODUCT_KEY.SPEC_NAME]
  const itemSpecName = defaultNames[PRODUCT_KEY.ITEM_SPEC_NAME]
  const specKeys = isTwoLayerAttr ? [specName, itemSpecName] : [specName]
  const [specValue, itemSpecValue] = _.map(specKeys, specKey => {
    // 無屬性從規格表中取出對應屬性的第一個值當作匯入商品的預設值
    return _.flow(
      attrs => _.find(attrs, { name: specKey }),
      attr => _.get(attr, 'values[0]')
    )(productAttrs)
  })
  if (isTwoLayerAttr) {
    const parentSpec = { name: specName, selectedValue: specValue }
    const spec = { name: itemSpecName, selectedValue: itemSpecValue }
    return { ...product, parentSpec, spec, attributes: productAttrs }
  }
  const spec = { name: specName, selectedValue: specValue }
  return { ...product, spec, attributes: productAttrs }
}

export const getProductIdParentMap = (rows) => {
  const productIdParentMap = _.reduce(rows, (map, row) => {
    const proudctId = row[PRODUCT_KEY.ID]
    const itemProudctId = row[PRODUCT_KEY.ITEM_ID]
    _.set(map, itemProudctId, proudctId)
    return map
  }, {})
  return productIdParentMap
}

export const getSpecSortListFromRows = rows => {
  const specSortList = _.flow(
    rows => _.map(rows, row => _.get(row, PRODUCT_KEY.SPEC_SELECTED_VALUE)),
    _.uniq
  )(rows)
  return specSortList
}

export const getAddedMapSelectedValueKey = (row, isTwoLayerAttr) => {
  const key = isTwoLayerAttr
    ? `${row[PRODUCT_KEY.SPEC_SELECTED_VALUE]}${row[PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE]}`
    : `${row[PRODUCT_KEY.SPEC_SELECTED_VALUE]}`
  return key
}

export const getModifiedTableRows = (proposal = {}, state) => {
  const {
    diff = {},
    // 完成提案時、送出提案時與檢視頁會取得的欄位，包含了原有、新增以及刪除的商品屬性
    products: modifiedProducts = [],
    // 檢視頁排序商品屬性參考 models 的順序
    models: modifiedModels = [],
    initiallyFetchedListing,
    listing: {
      shareMediaBetweenModels
    } = {}
  } = proposal
  const {
    // 新增的賣場屬性
    added: addedModels = [],
    // 被移除的既有賣場屬性
    removed: removedModels = [],
    // 被更新的既有賣場屬性
    updated: updatedModels = []
  } = diff
  const {
    listingSpecName,
    listingItemSpecName,
    isTwoLayerAttr
  } = state
  // 只有按下完成提案、送出提案或是檢視提案時才會有該提案被修改過的屬性項目
  if (_.isEmpty(modifiedProducts)) {
    return {}
  }

  const attrMap = getAttrMap(isTwoLayerAttr)
  const { productId } = attrMap
  const defaultNames = {
    [PRODUCT_KEY.SPEC_NAME]: listingSpecName,
    [PRODUCT_KEY.ITEM_SPEC_NAME]: listingItemSpecName
  }
  // 將新增與被移除的屬性做成對應表後面才能辨認哪一列屬於新增、移除或是既有的
  const [addedRowMap, removedRowMap, modifiedRowMap] = _.map(
    [addedModels, removedModels, modifiedModels],
    models => getModifiedRowMap(models, isTwoLayerAttr)
  )
  // 雙層賣場新增商品完成提案(draft)時不會有 item.id 所以要辨識哪個屬於新增的要用 selectedValue
  const addedRowMapBySelectedValue = getModifiedRowMap(addedModels, isTwoLayerAttr, model => {
    return getAddedMapSelectedValueKey(model, isTwoLayerAttr)
  })
  // 被更新的既有屬性結構與新增(added)移除(removed)不同，需要另外組出對應表
  const updatedRowMap = getUpdatedRowMap(updatedModels)
  const getCurrentProductId = row => _.get(row, productId)
  // 排序過的賣場屬性
  const sortedExistModels = getTableRowsFromListing({ models: modifiedModels }, true)
  const totalTableRows = _.flow(
    // 將全部商品轉成表格列格式
    products => _.map(products, (product) => {
      return getTableRowFromProduct(product, defaultNames, isTwoLayerAttr)
    }),
    // 以賣場屬性(models) 排序商品屬性，既有與新增的商品按照排序，被刪除的商品放最後
    rows => {
      const rowsGroupBySku = _.keyBy(rows, productId)
      const sortedRows = [
        // 新增與現存的商品屬性須按照排序
        ..._.map(sortedExistModels, row => {
          const currentProductId = _.get(row, productId)
          const outcome = _.get(updatedRowMap, `${_.get(row, PRODUCT_KEY.ID)}.outcome`, {})
          const origin = _.get(updatedRowMap, `${_.get(row, PRODUCT_KEY.ID)}.origin`, {})
          const itemOutcome = _.get(updatedRowMap, `${_.get(row, PRODUCT_KEY.ITEM_ID)}.outcome`, {})
          const itemOrigin = _.get(updatedRowMap, `${_.get(row, PRODUCT_KEY.ITEM_ID)}.origin`, {})
          const { spec, attributes, name } = outcome
          const {
            spec: itemSpec,
            parentSku,
            parentSpec,
            attributes: itemAttributes,
            name: itemName
          } = itemOutcome
          const isUpdate = _.some([outcome, itemOutcome], item => !_.isEmpty(item))
          const isOneLayerFisrtAttrUpdated = (
            !_.isUndefined(attributes) &&
            !_.isEqual(_.get(attributes, 0), _.get(origin, 'attributes.0'))
          )
          const isTwoLayerAttrsUpdated = _.times(2).map(index => {
            return (
              !_.isUndefined(itemAttributes) &&
              !_.isEqual(_.get(itemAttributes, index), _.get(itemOrigin, `attributes.${index}`))
            )
          })
          const targetName = isTwoLayerAttr ? itemName : name
          const isNameUpdated = (
            !_.isUndefined(targetName) &&
            !_.isEqual(targetName, _.get(isTwoLayerAttr ? itemOrigin : origin, 'name'))
          )
          const isSpecUpdated = (
            !_.isUndefined(parentSku || parentSpec) ||
            !_.isEqual(spec, _.get(origin, 'spec'))
          )
          const isItemSpecUpdated = (
            !_.isUndefined(itemSpec) &&
            !_.isEqual(itemSpec, _.get(itemOrigin, 'spec'))
          )
          const updatedField = {
            [attrMap.productName]: isNameUpdated,
            [PRODUCT_KEY.SPEC_SELECTED_VALUE]: isSpecUpdated,
            [PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE]: isItemSpecUpdated,
            [PRODUCT_KEY.ATTRIBUTES]: isTwoLayerAttr
              ? isTwoLayerAttrsUpdated
              : [isOneLayerFisrtAttrUpdated, false]
          }
          return {
            ..._.get(rowsGroupBySku, currentProductId),
            // 最終賣場的商品名稱、屬性與屬性編號要從 models 裡取得
            ..._.pick(
              row,
              [
                PRODUCT_KEY.ID,
                PRODUCT_KEY.ITEM_ID,
                PRODUCT_KEY.SPEC_NAME,
                PRODUCT_KEY.ITEM_SPEC_NAME,
                PRODUCT_KEY.SPEC_SELECTED_VALUE,
                PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE,
                PRODUCT_KEY.SPEC_VISIBLE,
                PRODUCT_KEY.ITEM_SPEC_VISIBLE,
                PRODUCT_KEY.NAME,
                PRODUCT_KEY.ITEM_NAME,
                PRODUCT_KEY.ATTRIBUTES
              ]
            ),
            isUpdate,
            updatedField
          }
        }),
        // 剩下刪除的商品屬性
        ..._.map(_.omit(rowsGroupBySku, _.map(sortedExistModels, productId)))
      ]
      return sortedRows
    }
  )(modifiedProducts)
  const tableRows = _.flow(
    // 非移除的屬性項目
    rows => _.filter(rows, row => {
      const productId = getCurrentProductId(row)
      // 不在刪除的屬性項目 (一般情況)
      const isProductInRemovedMap = (productId in removedRowMap)
      // 在修改的屬性項目 (商品重複刪除其中一筆，會有一個修改一個刪除)
      const isProductInRemovedAndUpdatedMap = (
        isProductInRemovedMap && (productId in updatedRowMap)
      )
      // 存在於提案裡的賣場屬性才保留（可修改贈品、加價購後 products 不只有賣場屬性的資料）
      const isModifiedMap = (productId in modifiedRowMap)
      return (!isProductInRemovedMap || isProductInRemovedAndUpdatedMap) && isModifiedMap
    }),
    filteredRows => _.map(filteredRows, (row, index) => {
      const rowWithIndex = { ...row, index }
      const addedDiff = (
        // 新增商品完成提案(draft)時不會有 id 所以要改成從 selectedValue 取得對應商品新增內容
        _.get(addedRowMapBySelectedValue, getAddedMapSelectedValueKey(row, isTwoLayerAttr)) ||
        // 匯入、調整賣場屬性可以從 id 取得對應商品新增內容
        _.get(addedRowMap, getCurrentProductId(row), {})
      )
      // 在 diff added 裡面的商品代表他是新增的
      if (!_.isEmpty(addedDiff)) {
        return getAddedDiffRow(rowWithIndex, addedDiff, initiallyFetchedListing, shareMediaBetweenModels)
      }
      return { ...rowWithIndex, [PRODUCT_KEY.IS_NEW]: false }
    }),
    rowsWithIndex => {
      const specSortList = isTwoLayerAttr
        ? _.uniq(
          _.map(rowsWithIndex, PRODUCT_KEY.SPEC_SELECTED_VALUE)
        )
        : []
      const defaultTableInfo = { specSortList }
      const { sortedTableRows } = getConvertedTableInfo(defaultTableInfo, rowsWithIndex, isTwoLayerAttr)
      return sortedTableRows
    }
  )(totalTableRows)
  // 移除之屬性商品的表格列
  const removedTableRows = _.flow(
    // 在 diff removed 裡面的商品代表他是既有被移除的
    rows => _.filter(rows, row => getCurrentProductId(row) in removedRowMap),
    filteredRows => _.map(filteredRows, (row, index) => {
      return ({
        ...row,
        // 被移除的商品他實際的屬性需要從賣場屬性裡面找，但是被移除後 models 就不會有這筆資料，
        // 所以要到 diff removed 裡面找出他的實際的賣場屬性
        ..._.pick(
          _.get(removedRowMap, getCurrentProductId(row), {}),
          [PRODUCT_KEY.SPEC_SELECTED_VALUE, PRODUCT_KEY.ITEM_SPEC_SELECTED_VALUE]
        ),
        index,
        [PRODUCT_KEY.IS_NEW]: false
      })
    })
  )(totalTableRows)

  return { tableRows, removedTableRows }
}

export const getAddedDiffRow = (rowWithIndex = {}, addedDiff, initiallyFetchedListing, shareMediaBetweenModels) => {
  const attrLevel = getAttrLevelFromListing(initiallyFetchedListing)
  const isTwoLayerAttr = (attrLevel === 2)
  const attrMap = getAttrMap(isTwoLayerAttr)
  const originModelMapBySpec = _.flow(
    () => _.get(initiallyFetchedListing, 'models', []),
    originModels => _.keyBy(originModels, 'spec.selectedValue')
  )()
  // 屬性項目調整的表格列
  // 完成提案時新增商品還不會產生商品編號，這時候重新產生的表格列中新增商品會沒有 sku & item sku
  // 為了讓返回後可以正常執行產生一個 fake Id 加上流水號萬一要返回編輯時可以有 id 辨識屬於哪個商品
  const runtimeFakeId = Date.now()
  // 在 diff added 裡面的商品代表他是新增的
  const fieldsFromAddedDiff = _.pick(addedDiff, [
    PRODUCT_KEY.IMAGES,
    PRODUCT_KEY.ATTRIBUTES,
    attrMap.stock,
    attrMap.barcode,
    attrMap.productPartNo,
    attrMap.productName
  ])
  let newProductProps = {}
  if (PRODUCT_KEY.ATTRIBUTES in fieldsFromAddedDiff) {
    let originModel
    let editable
    let fakeIdProps = {}
    let attrProps = {}
    const [layerOneAttribute, layerTwoAttribute] = _.flow(
      () => _.get(fieldsFromAddedDiff, `${PRODUCT_KEY.ATTRIBUTES}`, []),
      attributes => _.map(attributes, 'values')
    )()
    const fakeProductId = getFakeProductId(`${runtimeFakeId}${rowWithIndex.index}`)
    if (isTwoLayerAttr) {
      // 雙層賣場如果是共用商品找同一分群的第一筆的商品圖
      originModel = _.get(originModelMapBySpec, rowWithIndex[PRODUCT_KEY.SPEC_SELECTED_VALUE], {})
      // 從預設同一分群是否有商品判斷是否為共用商品
      editable = _.isEmpty(originModel)
      // 預設雙層給兩個 fakeId 後續第一層如果有同一分群會以該分群取代第一層 id
      fakeIdProps = { [PRODUCT_KEY.ID]: fakeProductId, [PRODUCT_KEY.ITEM_ID]: fakeProductId }
      // 雙層屬性賣場會有兩個屬性
      attrProps = {
        [PRODUCT_KEY.ATTR]: layerOneAttribute,
        [PRODUCT_KEY.ITEM_ATTR]: layerTwoAttribute
      }
    } else {
      // 單層賣場如過是共用商品找 models 第一筆的商品圖
      originModel = _.get(initiallyFetchedListing, 'models[0]')
      // 從提案儲存當下的賣場 shareMediaBetweenModels 判斷是否為共用商品
      editable = !shareMediaBetweenModels
      // 單層給一個 fakeId 只有送出提案到檢視頁才會被取代
      fakeIdProps = { [PRODUCT_KEY.ID]: fakeProductId }
      // 丹層屬性賣場會有一個屬性
      attrProps = { [PRODUCT_KEY.ATTR]: layerOneAttribute }
    }
    // 預設非共用圖片可以直接取得
    const currentImages = _.flow(
      () => _.get(
        fieldsFromAddedDiff,
        PRODUCT_KEY.IMAGES,
        // 共用圖片從原本賣場的商品裡找到對應此商品第一層屬性的商品，即共用商品圖
        _.get(originModel, PRODUCT_KEY.IMAGES, [])
      ),
      // 按照 order 由小排到大，每個 order 按照圖片大小由大排到小
      images => _.orderBy(images, ['order', 'height'], ['asc', 'desc']),
      // 每個 order 取的一筆
      sortedImages => _.uniqBy(sortedImages, 'order')
    )()
    const imagesProps = _.isEmpty(currentImages)
      ? {}
      : {
        [PRODUCT_KEY.NEW_PRODUCT_IMAGES]: currentImages,
        [PRODUCT_KEY.IMAGES]: currentImages,
        image: _.get(currentImages, '0.url')
      }
    newProductProps = {
      ...imagesProps,
      ...fakeIdProps,
      ...attrProps,
      attrLevel,
      // 設定分群
      ...(isTwoLayerAttr && { collection: rowWithIndex[PRODUCT_KEY.SPEC_SELECTED_VALUE] }),
      // 表格列 New Icon 前綴
      [PRODUCT_KEY.IS_NEW]: true,
      // 判斷是否屬於既有分群底下的商品
      [PRODUCT_KEY.EDITABLE]: editable,
      // 可以展開檢視商品圖片
      isSpecSelected: true
    }
  }
  return {
    // 預設 images 會先寫入，如果是共用商品圖會在 newProductProps 取代掉
    ...fieldsFromAddedDiff,
    // 要是實際上有 sku & item sku 會讓實際的 sku 取代掉假的
    ...newProductProps,
    ..._.omit(rowWithIndex, [
      // images 會在 newProductProps 重新計算
      PRODUCT_KEY.IMAGES,
      // barcode 會在 added 取得
      PRODUCT_KEY.BARCODE,
      PRODUCT_KEY.ITEM_BARCODE
    ])
  }
}

export const getSortedSpecModelTableInfo = (updatedSpecRowMapById, sortedSpecRows) => {
  const dataSource = []
  const updatedSpecMapById = {}
  const updatedAttributesMapById = {}
  for (const row of sortedSpecRows) {
    const productId = _.get(row, PRODUCT_KEY.ID)
    const field = PRODUCT_KEY.SPEC_SELECTED_VALUE
    const attributesField = PRODUCT_KEY.ATTRIBUTES
    const sortFormFieldKey = `${productId}-${field}`
    const attributesFormFieldKey = `${productId}-${attributesField}`
    const prevSpecSelectedValue = _.get(row, field)
    const newSpecSelectedValue = _.get(updatedSpecRowMapById, sortFormFieldKey)
    const prevAttributes = _.get(row, attributesField)
    const newAttributes = _.get(updatedSpecRowMapById, attributesFormFieldKey)
    // 將 spec sort modal 裡修改的第一層屬性名稱覆寫進原本的欄位
    dataSource.push({
      ...row,
      [field]: newSpecSelectedValue
    })
    // 如果資料有修改過記錄到對應表上，提供後面寫入 model table 時判斷哪些分群商品的第一層屬性需要批次修改
    const isSpecSelectedValueChanged = prevSpecSelectedValue !== newSpecSelectedValue
    if (isSpecSelectedValueChanged) {
      _.set(updatedSpecMapById, productId, { prevSpecSelectedValue, newSpecSelectedValue })
    }
    const isAttributesChanged = !_.isEqual(prevAttributes, newAttributes)
    if (isAttributesChanged) {
      _.set(updatedAttributesMapById, productId, { prevAttributes, newAttributes })
    }
  }
  let sortedSpecDataSource = dataSource
  const uniqDataSource = _.uniqBy(dataSource, PRODUCT_KEY.SPEC_SELECTED_VALUE)
  // 如果 dataSource 跟 uniqDataSource 不同代表有多列屬性項目相同需要被合併
  if (!_.isEqual(dataSource, uniqDataSource)) {
    const dataSourceSelectedValueMap = _.keyBy(uniqDataSource, PRODUCT_KEY.SPEC_SELECTED_VALUE)
    sortedSpecDataSource = _.map(dataSource, item => {
      const specSelectedValue = _.get(item, PRODUCT_KEY.SPEC_SELECTED_VALUE)
      const newProductId = _.get(dataSourceSelectedValueMap, `${specSelectedValue}.${PRODUCT_KEY.ID}`)
      if (_.isUndefined(newProductId)) {
        return item
      }
      return { ...item, [PRODUCT_KEY.ID]: newProductId }
    })
  }
  return { sortedSpecDataSource, updatedSpecMapById, updatedAttributesMapById }
}

export const PRODUCT_ID_FAKE_PREFIX = 'fake_'

export const getFakeProductId = key => `${PRODUCT_ID_FAKE_PREFIX}${key}`

export const checkIsFakeProductId = id => new RegExp(`^${PRODUCT_ID_FAKE_PREFIX}`).test(id)

export const getIsAllowNewProduct = listing => {
  return (
    // 有取得賣場資料後才能新增商品(否則不知道預設商品是單層或雙層屬性)
    !_.isEmpty(listing) &&
    // 非快速到貨才允許新增商品
    !(_.get(listing, 'skuDetail.shipType.id') === PRODUCT_SHIP_TYPE.EXPRESS_24HR)
  )
}

export const getListingParamsFromForm = form => {
  const applicant = _.get(form, 'applicant')
  const listingId = _.get(form, 'listing.id')
  const params = {
    applicant,
    listing: { id: listingId }
  }
  return params
}

export const getProductIdsFromListing = listing => {
  const attrLevel = getAttrLevelFromListing(listing)
  const models = _.get(listing, 'models', [])
  const isNoModelListing = _.get(models, '0.sku') === -1
  if (isNoModelListing) {
    return []
  }

  if (attrLevel === 1) {
    const productIds = _.map(models, 'sku')
    return _.uniq(productIds)
  }

  const itemProductIds = _.reduce(models, (collect, model) => {
    const items = _.get(model, 'items', [])
    const newItemProductIds = _.map(items, 'sku')
    collect = [...collect, ...newItemProductIds]
    return collect
  }, [])
  return _.uniq(itemProductIds)
}

export const getListingWithAttrsFromProducts = (listing, products) => {
  const attrLevel = getAttrLevelFromListing(listing)
  const productMapBySku = _.keyBy(products, 'sku')
  const models = _.get(listing, 'models', [])
  const isTwoLayerAttr = attrLevel === 2

  const newModels = models.map((model = {}) => {
    const { sku, items = [] } = model
    const defaultAttributes = _.get(productMapBySku, `${sku}.structuredData.attributes`, [])
    const defaultAttributeValues = _.get(defaultAttributes, '0.values')
    const attributeValues = _.size(defaultAttributeValues) === 1 ? defaultAttributeValues : []
    const attributes = _.set([...defaultAttributes], '0.values', attributeValues)
    const newItems = _.map(items, item => {
      const itemSku = _.get(item, 'sku')
      const defaultItemAttributes = _.get(productMapBySku, `${itemSku}.structuredData.attributes`, [])
      const defaultItemAttributeValues = _.get(defaultItemAttributes, '0.values')
      const itemAttributeValues = _.size(defaultItemAttributeValues) === 1 ? defaultItemAttributeValues : []
      const itemAttributes = _.set([...defaultItemAttributes], '0.values', itemAttributeValues)
      const newItem = { ...item, attributes: itemAttributes }
      return newItem
    })
    const newModel = {
      ...model,
      ...(!isTwoLayerAttr && { attributes }),
      ...(isTwoLayerAttr && { items: newItems })
    }
    return newModel
  })
  const newListing = { ...listing, models: newModels }
  return newListing
}

// 根據規格表內定義的規格選項，將預期外的屬性項目清空
export const getTableRowsWithExpectedAttrValues = (tableRowsFromListing, specDetailMapBySpec) => {
  // 取得規格表內定義的規格選項
  const optionMaps = _.flow(
    () => _.get(tableRowsFromListing, '0.attributes', []),
    attributes => _.map(attributes, attribute => {
      const attributeName = _.get(attribute, 'name')
      const specDetailMap = _.get(specDetailMapBySpec, attributeName, {})
      // 自訂、文字匡屬性不會有選項直接跳過
      const isAllowCustomAttr = [
        ATTRIBUTE_CONSTRAINTS_TYPE.CUSTOM,
        ATTRIBUTE_CONSTRAINTS_TYPE.TEXT
      ].includes(_.get(specDetailMap, 'constraints.type'))
      if (isAllowCustomAttr) {
        return null
      }

      const optionMap = _.keyBy(_.get(specDetailMap, 'options', []), 'value')
      return optionMap
    })
  )()

  return _.map(tableRowsFromListing, row => {
    const attributes = _.get(row, 'attributes', [])
    return {
      ...row,
      // 屬性有限制只能用規格裡的選項時這裡會判斷並剔除非預期數值
      [PRODUCT_KEY.ATTRIBUTES]: _.map(attributes, (attribute, index) => {
        const attrValue = _.get(attribute, 'values[0]')
        const optionMap = _.get(optionMaps, index)
        // 規格表沒定義選項
        if (_.isEmpty(optionMap)) {
          return attribute
        }

        const isExpectedAttrValue = attrValue in optionMap
        // 預期選項時不變更
        if (isExpectedAttrValue) {
          return attribute
        }

        // 非預期選項時清空屬性項目數值
        return {
          ...attribute,
          values: []
        }
      })
    }
  })
}
